import React, { useEffect, useState } from 'react';
import { Table, rem, createStyles, UnstyledButton, Group, Text, Center, Flex, ScrollArea, Skeleton, Box, Image, useMantineTheme, Overlay } from '@mantine/core';
import { IconChevronUp, IconChevronDown, IconSelector } from '@tabler/icons-react';
import { keys } from '@mantine/utils';
import { notifications } from '@mantine/notifications';
import { getBenefitLevels } from '../../../services/api.service';
import { useDispatch, useSelector } from 'react-redux';

// Custom styles
const useStyles = createStyles((theme) => ({
	th: {
		padding: '0 !important',
	},
	control: {
		width: '100%',
		padding: `${theme.spacing.xs} ${theme.spacing.md}`,
		'&:hover': {
			backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[1],
		},
	},
	icon: {
		width: rem(21),
		height: rem(21),
		borderRadius: rem(21),
	},
}));

const BenefitLevelsList = ({ benefit, reload, setEdit }) => {

	// State and hooks
	const [isLoading, setIsLoading] = useState(true);
	const [benefitLevels, setBenefitLevels] = useState([]);
	const [sortedData, setSortedData] = useState([]);
	const [sortBy, setSortBy] = useState(null);
	const [reverseSortDirection, setReverseSortDirection] = useState(false);
	const { classes } = useStyles();
	const theme = useMantineTheme();
	const dispatch = useDispatch();
	const { isEditing } = useSelector(state => state.editBenefitLevel);
	const [prevIsEditing, setPrevIsEditing] = useState(false);

	// On load
	useEffect(() => {
		getBenefitLevelsList();
		// eslint-disable-next-line
	}, []);

	// Listen to reload changes
	useEffect(() => {
		if (reload !== 0) getBenefitLevelsList();
		// eslint-disable-next-line
	}, [reload]);

	// Listen to editing changes
	useEffect(() => {
		// Trigger only when it turns from true to false
		if (prevIsEditing === true && isEditing === false) {
			getBenefitLevelsList();
		}
		setPrevIsEditing(isEditing);
		// eslint-disable-next-line
	}, [isEditing]);

	// Get benefit levels
	const getBenefitLevelsList = () => {

		// Get
		getBenefitLevels(benefit.id, function (response) {

			// Set benefit levels and initial sorted data, remove loader
			setBenefitLevels(response.data);
			setSortedData(response.data);
			setIsLoading(false);

		}, function (error) {

			// Remove loader
			setIsLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	// TH component
	const Th = ({ children, reversed, sorted, onSort }) => {
		const { classes } = useStyles();
		return (
			<th className={classes.th}>
				<UnstyledButton onClick={onSort} className={classes.control}>
					<Group position="apart">
						<Text fw={700} fz="sm">
							{children}
						</Text>
						<Center className={classes.icon}>
							{sorted ?
								(reversed ?
									<IconChevronUp size="0.9rem" stroke={1.5} />
									:
									<IconChevronDown size="0.9rem" stroke={1.5} />
								)
								: <IconSelector size="0.9rem" stroke={1.5} />}
						</Center>
					</Group>
				</UnstyledButton>
			</th>
		);
	}

	// Build table head
	const ths = (
		<tr>
			<Th
				sorted={sortBy === 'id'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('id')}
			>
				ID
			</Th>
			<Th
				sorted={sortBy === 'points'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('points')}
			>
				Puntos
			</Th>
			<Th
				sorted={sortBy === 'days'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('days')}
			>
				Días
			</Th>
			<Th
				sorted={sortBy === 'maxPerUser'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('maxPerUser')}
			>
				Usos Máximos
			</Th>
			<Th
				sorted={sortBy === 'level'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('level')}
			>
				Nivel
			</Th>
			<Th
				sorted={sortBy === 'image'}
				reversed={reverseSortDirection}
				onSort={() => setSorting('image')}
			>
				Imagen
			</Th>
		</tr>
	);

	// Build rows
	const rows = sortedData.map((element) => (

		// Show only if connected to FK
		element.benefit ?
			<tr
				style={{ cursor: 'pointer' }}
				key={element.id}
				onClick={() => toggleEditElement(element)}
			>
				<td width={100}>
					{element.id}
				</td>
				<td>
					{element.points}
				</td>
				<td>
					{element.days}
				</td>
				<td>
					{element.maxPerUser}
				</td>
				<td>
					({element.level.id}) {element.level.name}
				</td>
				<td width={150}>
					<Image
						src={element.image}
						className={classes.imagePreview}
						radius={theme.radius.sm}
						maw={100}
					/>
				</td>
			</tr>
			: null

	));

	// Get current sort
	const setSorting = (field) => {
		const reversed = field === sortBy ? !reverseSortDirection : false;
		setReverseSortDirection(reversed);
		setSortBy(field);
		setSortedData(sortData(benefitLevels, { sortBy: field, reversed }));
	};

	// Filter and return from query/sort
	const filterData = (data) => {
		return data.filter((item) => keys(data[0]).some((key) => item[key].toString().toLowerCase()));
	}

	// Sort data
	const sortData = (data, payload) => {

		// Assign
		const { sortBy } = payload;

		// No filter
		if (!sortBy) { return filterData(data); }

		// Return filtered data
		return filterData(
			[...data].sort((a, b) => {
				if (payload.reversed) {
					return b[sortBy].toString().localeCompare(a[sortBy]);
				}

				return a[sortBy].toString().localeCompare(b[sortBy]);
			})
		);

	}

	// Toggle edit element
	const toggleEditElement = (element) => {
		dispatch.editBenefitLevel.toggleEditing(true);
		dispatch.editBenefitLevel.setBenefitLevel(element);
	}

	return (
		<>
			<Box>
				{/* Content */}
				<Flex
					direction="column"
					justify="space-between"
				>
					{/* Group */}
					<Flex direction="column">
						{/* Table */}
						<ScrollArea>
							{isEditing ?
								<Overlay
									blur={2}
									color={theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white}
								></Overlay>
								: null}
							{isLoading ?
								<>
									<Skeleton height={40} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
									<Skeleton height={32} mt={12} radius="sm" />
								</>
								:
								<Table
									highlightOnHover
									sx={{ flex: 2 }}
								>
									<thead>{ths}</thead>
									<tbody>{rows.length > 0 ? (
										rows
									) : (
										<tr>
											<td colSpan={7}>
												<Text
													weight={500}
													align="center"
													my="xl"
													color="dimmed"
												>
													Sin resultados
												</Text>
											</td>
										</tr>
									)}</tbody>
								</Table>
							}
						</ScrollArea>
					</Flex>
				</Flex>
			</Box>
		</>
	);

}

export default BenefitLevelsList;
